import { LockMarks, ManifestLocksDTO, PageLockDTO, PageLockStateDTO } from "../models/LockDTO";

import { AgenticEnvelope } from "../../documentation/models/services/AgenticMetadataDTO";
import { ConfigurationProvider } from "../../../lib/infrastructure/configuration/ConfigurationProvider";
import { HttpClient } from "../../../lib/infrastructure/http/HttpClient";
import { UrlUtility } from "../../../lib/utils/UrlUtility";
import { buildLockMarks } from "../utils/lockMarks";

const Route = (path: string) =>
    UrlUtility.join(ConfigurationProvider.getConfiguration().App.BackendUrl, path);

const ClientHeaders = { "X-Orbital-Client": "ark-web" };

const spacePath = (spaceId: string | number) => `/api/agentic/v1/spaces/${encodeURIComponent(String(spaceId))}`;

/** The lock view of a space, from its manifest. */
export interface SpaceLocks {
    /** Page id → badge mark (locked pages only). */
    marks: LockMarks;
    /** Page id → lock, for the pages that carry one. */
    locks: Record<number, PageLockDTO>;
    /** The server could not evaluate the caller's lock grants: locked pages are missing from the listings. */
    partial: boolean;
}

/** What the lock screens need; components take it as a prop so tests can pass a fake. */
export interface ILockService {
    getManifestLocks(spaceId: string | number): Promise<SpaceLocks>;
    getLock(spaceId: string | number, pageId: string | number): Promise<PageLockStateDTO>;
    lock(spaceId: string | number, pageId: string | number, reason?: string): Promise<PageLockDTO>;
    unlock(spaceId: string | number, pageId: string | number): Promise<void>;
    listLocks(spaceId: string | number): Promise<PageLockDTO[]>;
}

export type LockErrorKind = "not-found" | "forbidden" | "identity-unavailable" | "other";

/**
 * Classifies an Ark.Core error. 404 = hidden or absent (Ark never says "forbidden" for a page); 403 = the caller lacks the lock
 * permission for this action; 503 with `identity-unavailable` (facade envelope) or `identity_unavailable` (legacy routes and the
 * Identity SDK) = Identity Core could not be asked past the grace window.
 */
export function lockErrorKind(err: any): LockErrorKind {
    const status = err?.response?.status;
    if (status === 404) return "not-found";
    if (status === 403) return "forbidden";
    if (status === 503) {
        const error = err?.response?.data?.error;
        const code = typeof error === "string" ? error : error?.code;
        if (code === "identity-unavailable" || code === "identity_unavailable") return "identity-unavailable";
    }
    return "other";
}

/** The facade's error message when it sent one, else the transport's. */
export function lockErrorMessage(err: any): string {
    const error = err?.response?.data?.error;
    return (typeof error === "object" && error?.message) || err?.response?.data?.message || err?.message || "Unexpected error";
}

function unwrap<T>(envelope: AgenticEnvelope<T> | undefined): T {
    if (!envelope) throw new Error("Empty response from the server.");
    if (envelope.error) throw new Error(envelope.error.message || envelope.error.code);
    return envelope.data as T;
}

/** Page locks through Ark.Core's agentic facade (OrbitalPlan/18 D6), with the Ark token only (no Identity scope). */
export class LockService implements ILockService {
    async getManifestLocks(spaceId: string | number): Promise<SpaceLocks> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<ManifestLocksDTO>>({
            method: "GET",
            url: Route(`${spacePath(spaceId)}/manifest`),
            headers: ClientHeaders,
        });
        const manifest = unwrap(res.data) ?? {};
        const locks: Record<number, PageLockDTO> = {};
        for (const p of manifest.pages ?? []) if (p.lock) locks[p.id] = p.lock;
        return { marks: buildLockMarks(manifest.pages), locks, partial: !!manifest.partial };
    }

    async getLock(spaceId: string | number, pageId: string | number): Promise<PageLockStateDTO> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<PageLockStateDTO>>({
            method: "GET",
            url: Route(`${spacePath(spaceId)}/pages/${encodeURIComponent(String(pageId))}/lock`),
            headers: ClientHeaders,
        });
        return unwrap(res.data);
    }

    async lock(spaceId: string | number, pageId: string | number, reason?: string): Promise<PageLockDTO> {
        const trimmed = reason?.trim();
        const res = await HttpClient.sessionRequest<AgenticEnvelope<PageLockDTO>>({
            method: "PUT",
            url: Route(`${spacePath(spaceId)}/pages/${encodeURIComponent(String(pageId))}/lock`),
            data: trimmed ? { reason: trimmed } : {},
            headers: ClientHeaders,
        });
        return unwrap(res.data);
    }

    async unlock(spaceId: string | number, pageId: string | number): Promise<void> {
        await HttpClient.sessionRequest({
            method: "DELETE",
            url: Route(`${spacePath(spaceId)}/pages/${encodeURIComponent(String(pageId))}/lock`),
            headers: ClientHeaders,
        });
    }

    async listLocks(spaceId: string | number): Promise<PageLockDTO[]> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<PageLockDTO[]>>({
            method: "GET",
            url: Route(`${spacePath(spaceId)}/locks`),
            headers: ClientHeaders,
        });
        return unwrap(res.data) ?? [];
    }
}

let defaultInstance: LockService | undefined;

/** The shared instance the screens use when no service prop is passed. */
export function lockService(): ILockService {
    if (!defaultInstance) defaultInstance = new LockService();
    return defaultInstance;
}
